"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TradesProcessor = void 0;
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_quotation_lib_1 = require("trade_quotation_lib");
const Trades_1 = require("../../../apis/Trades");
const SimpleFutures_1 = require("../../../component/SimpleFutures");
const interface_1 = require("../../../interface");
/**
 * Trades 模块
 */
var TradesProcessor;
(function (TradesProcessor) {
    /**
     * 获取图表渲染的一些数据
     * @param data[] 一定保证是 item.timestamp 增序排序数组
     * @param timeRange 相对 data[data.length - 1].timestamp 的偏移量
     * @returns RenderData | undefined
     */
    const getRenderData = (data, timeRange) => {
        const dataLength = data.length;
        // 数据量少于 1 就直接返回
        if (dataLength < 1) {
            return;
        }
        // 最大，最小点默认为最后一个点
        let minPoint = data[dataLength - 1];
        let maxPoint = minPoint;
        // 从最后一个点往左遍历
        for (let index = dataLength - 1; index >= 0; index--) {
            const currentPoint = data[index];
            const currentTimeOffset = data[dataLength - 1].timestamp - currentPoint.timestamp;
            const isPointInView = currentTimeOffset <= timeRange;
            // 当前点在屏幕视图中
            if (isPointInView) {
                // 设置最高、最低点
                if (currentPoint.price > maxPoint.price) {
                    maxPoint = currentPoint;
                }
                else if (currentPoint.price < minPoint.price) {
                    minPoint = currentPoint;
                }
            }
            else {
                // 遇到不在视图中的点，停止循环
                break;
            }
        }
        return {
            maxPoint,
            minPoint,
            maxYAxis: maxPoint.price,
            minYAxis: minPoint.price,
        };
    };
    /**
     * 把全量数据、当前用户的交易数（买卖点）据融合。本质上是基于买卖点的数据，给全亮数据添加 type 属性。时间复杂度 O(M + N)
     * @param tradesPoints 交易数据（保证数据按照 timestamp 增序排序）
     * @param buySellPoints 买卖点数据（保证数据按照 timestamp 增序排序）
     * @param sampleRate 交易数据之间的时间间隔
     */
    const mergedWithCurrentUserTrades = (tradesPoints, buySellPoints, sampleRate) => {
        let tradeIndex = tradesPoints.length - 1;
        for (let buySellIndex = buySellPoints.length - 1; buySellIndex >= 0; buySellIndex--) {
            const buySell = buySellPoints[buySellIndex];
            for (; tradeIndex >= 0;) {
                const tradePoint = tradesPoints[tradeIndex];
                const nextTradePoint = tradesPoints[tradeIndex + 1];
                const prevTradePoint = tradesPoints[tradeIndex - 1];
                const rightTime = tradePoint.timestamp +
                    ((nextTradePoint === null || nextTradePoint === void 0 ? void 0 : nextTradePoint.timestamp) ? (nextTradePoint.timestamp - tradePoint.timestamp) / 2 : sampleRate * 2);
                const leftTime = tradePoint.timestamp -
                    ((prevTradePoint === null || prevTradePoint === void 0 ? void 0 : prevTradePoint.timestamp) ? (tradePoint.timestamp - prevTradePoint.timestamp) / 2 : sampleRate * 2);
                if (buySell.timestamp > rightTime) {
                    break;
                }
                else if (buySell.timestamp > leftTime) {
                    if (tradesPoints[tradeIndex].type) {
                        if (tradesPoints[tradeIndex].type !== "both" && tradesPoints[tradeIndex].type !== buySell.side) {
                            tradesPoints[tradeIndex].type = "both";
                        }
                    }
                    else {
                        tradesPoints[tradeIndex].type = buySell.side;
                    }
                    break;
                }
                else {
                    tradeIndex -= 1;
                }
            }
        }
        return tradesPoints;
    };
    /**
     * 获取交易线性图的数据
     *
     * @param options - 交易选项：Trades.TradeOptions
     * @returns Trades.TradeLineData
     */
    TradesProcessor.useTradeLineData = (options) => {
        /** 对外返回的 Subject 对象 */
        const dataSubject = (0, react_1.useRef)(new rxjs_1.Subject());
        /** 还未融合的当前用户的买卖点数据（融合后需要手动清空值） */
        const unMergedCurrentUserTradeData = (0, react_1.useRef)();
        /** 获取当前用户的买卖点的接口请求参数（用函数而不是对象字面两的原因是：内部的参数不会自己变化，需要调用的时候实时获取） */
        const getCurrentUserTradeParams = (0, react_1.useCallback)(() => {
            const now = Date.now();
            return {
                start_time: now - options.screenTimeRange,
                end_time: now,
                base: options.base,
                quote: options.quote,
            };
        }, [options.base, options.quote, options.screenTimeRange]);
        /** 当前用户的买卖点的订阅数据流 */
        const currentUserTrades$ = SimpleFutures_1.SimpleFuturesProcessor.useCurrentUserTradesSubject(getCurrentUserTradeParams, Boolean(options.showHistoriesOrder && options.isActive));
        (0, react_1.useEffect)(() => {
            /** 用于存放从 WebSocket 接收到的最新数据（每次使用过后，会被清空） */
            let wssPushedData;
            /** 已经向外推送过的数据（C 端已消费的数据） */
            let broadcastedData = [];
            /** C 端需要用到的一些，需要额外计算的渲染数据 */
            let calculatedRenderData;
            /** 数据广播函数 */
            const broadcast = (data, renderData, keepWssData) => {
                var _a;
                /** 是否需要融合数据 */
                const needMergeCurrentUserTradeData = ((_a = unMergedCurrentUserTradeData.current) === null || _a === void 0 ? void 0 : _a.length) && options.showHistoriesOrder;
                const newData = needMergeCurrentUserTradeData
                    ? mergedWithCurrentUserTrades(data || [], unMergedCurrentUserTradeData.current, options.wss_sample_rate)
                    : data || [];
                // 数据融合后就清空（避免重复融合）
                unMergedCurrentUserTradeData.current = undefined;
                /** 最新的点是否有交易点（如果有的话，需要制造一个新的点，插入到倒数第二的位置，因为最新的点展示买卖点不合适，会被最新价格的闪动点挡住） */
                const latestData = newData[newData.length - 1];
                if ((latestData === null || latestData === void 0 ? void 0 : latestData.type) && newData.length >= 2) {
                    const secondData = newData[newData.length - 2];
                    if (secondData.isFakePoint) {
                        secondData.type = latestData.type;
                        latestData.type = undefined;
                    }
                    else {
                        // 1. 创造一个没有 type 的最新点（它的其他值和现在的最新点一样）
                        newData.push(Object.assign(Object.assign({}, latestData), { type: undefined }));
                        // 2. 修改倒数第二个点的时间戳为：最新点和倒数第三个点的时间戳的一半
                        const fakePointTime = (latestData.timestamp + newData[newData.length - 3].timestamp) / 2;
                        newData[newData.length - 2].timestamp = fakePointTime;
                        newData[newData.length - 2].isFakePoint = true;
                    }
                }
                calculatedRenderData = renderData || getRenderData(newData, options.screenTimeRange);
                dataSubject.current.next({ data: newData, renderData: calculatedRenderData });
                broadcastedData = newData;
                if (!keepWssData) {
                    // 清空 wss 的推送数据，准备接收下一个 wss 推送
                    wssPushedData = undefined;
                }
            };
            // 外层数据可能还没有来得及订阅 dataSubject，所以需要初始值需要延迟到下一个 tick 发出
            setTimeout(() => {
                // 每次当前 useEffect 依赖变化，都要清空数据
                broadcast([]);
            });
            // 未激活状态不进行任何订阅
            if (!options.isActive) {
                return;
            }
            const currentUserTradesSubscription = currentUserTrades$.subscribe({
                next: (data) => {
                    // 赋值新的买卖点数据
                    unMergedCurrentUserTradeData.current = data;
                    if (broadcastedData.length) {
                        broadcast(broadcastedData, calculatedRenderData, true);
                    }
                },
                error: () => { },
            });
            // 订阅 wss 最新交易数据
            const wssSubscription = (0, trade_quotation_lib_1.getGlobalQuotationSocketMgr)()
                .getTradesWebSocketV2()
                .getTradesObservable(options.exchange, options.isMarkPrice ? `${options.base}.M` : options.base, options.quote)
                .subscribe({
                next: (data) => {
                    if (data.length) {
                        const newComing = data[data.length - 1];
                        if (newComing.timestamp >= ((wssPushedData === null || wssPushedData === void 0 ? void 0 : wssPushedData.timestamp) || 0)) {
                            // 如果新的推送数据比上一个时间戳合理，就把数据存起来
                            wssPushedData = newComing;
                            // 如果是长时间模式 && 已经有数据，就修改最新一个点的价格为 wss 推送价格
                            if (options.isLongRangeMode && broadcastedData.length && calculatedRenderData) {
                                // 更新最新的点的价格
                                broadcastedData[broadcastedData.length - 1].price = newComing.price;
                                // 修改最新点的价格，渲染数据要重新计算 && 不清空已有的 wss 推送数据
                                broadcast(broadcastedData, undefined, true);
                            }
                        }
                    }
                },
                error: () => { },
            });
            // 从 API 获取历史数据
            const now = Date.now();
            const historyRequest = (0, rxjs_1.from)(options.isLongRangeMode
                ? Trades_1.TradesAPI.getKlineHistory({
                    base: options.isMarkPrice ? `${options.base}.M` : options.base,
                    quote: options.quote,
                    market: options.exchange,
                    interval: options.intervalType || interface_1.LineIntervalType["_1h"],
                    start: Math.floor((now - options.screenTimeRange) / 1000),
                    end: Math.floor(now / 1000),
                    from: options.requestClientType || "app",
                })
                : Trades_1.TradesAPI.getHistory({
                    base: options.base,
                    quote: options.quote,
                    sample_rate: options.liveLineSampleRate,
                    time_offset: options.screenTimeRange,
                })).pipe((0, operators_1.tap)((histories) => {
                // 拿到历史数据，马上进行一次广播
                broadcast(histories);
            }));
            /** 推送新的 wss 的数据 (每次轮训需要调用的函数) */
            const pushWssData = () => {
                var _a, _b, _c, _d, _e;
                // 即将增加的一条新数据
                const newComing = (() => {
                    // 历史数据中的最新数据
                    const latest = broadcastedData[broadcastedData.length - 1];
                    if (latest) {
                        if (wssPushedData && wssPushedData.timestamp > latest.timestamp) {
                            return wssPushedData;
                        }
                        return {
                            timestamp: latest.timestamp + options.wss_sample_rate,
                            price: latest.price,
                        };
                    }
                    return wssPushedData;
                })();
                if (!newComing) {
                    return;
                }
                // 结合 wss 的推送数据得到新的数据
                let newData = broadcastedData.concat(newComing);
                // 删除 newData 多余的(左侧屏幕外)数据
                if (newData.length >= 2) {
                    const dataTimeRange = newData[newData.length - 2].timestamp - ((_a = newData[0]) === null || _a === void 0 ? void 0 : _a.timestamp);
                    const isDataTimeOverRange = dataTimeRange > options.screenTimeRange;
                    if (isDataTimeOverRange) {
                        const deleteTimestamp = newData[newData.length - 2].timestamp - options.screenTimeRange;
                        const lastPointIndex = newData.findIndex((item) => item.timestamp >= deleteTimestamp);
                        if (newData[lastPointIndex].timestamp === deleteTimestamp) {
                            newData = newData.slice(lastPointIndex);
                        }
                        else {
                            newData = newData.slice(lastPointIndex - 1);
                        }
                    }
                }
                // 根据新的数据，计算现在的 renderData
                if (calculatedRenderData) {
                    /** 视图可见范围内，最小的时间戳 */
                    const minTimestamp = newData[newData.length - 1].timestamp - options.screenTimeRange;
                    /** 当最低点、最高点被隐藏起来的时候 */
                    const isMinMaxPointHidden = [(_b = calculatedRenderData.minPoint) === null || _b === void 0 ? void 0 : _b.timestamp, (_c = calculatedRenderData.maxPoint) === null || _c === void 0 ? void 0 : _c.timestamp].some((timestamp) => (timestamp || 0) < minTimestamp);
                    if (isMinMaxPointHidden) {
                        // 按照历史数据循环一遍得到初始化的值
                        calculatedRenderData = getRenderData(newData, options.screenTimeRange);
                    }
                    else if (newComing.price >= (((_d = calculatedRenderData.maxPoint) === null || _d === void 0 ? void 0 : _d.price) || 0)) {
                        calculatedRenderData.maxPoint = newComing;
                        calculatedRenderData.maxYAxis = newComing.price;
                    }
                    else if (newComing.price <= (((_e = calculatedRenderData.minPoint) === null || _e === void 0 ? void 0 : _e.price) || 0)) {
                        calculatedRenderData.minPoint = newComing;
                        calculatedRenderData.minYAxis = newComing.price;
                    }
                }
                else {
                    calculatedRenderData = {
                        maxPoint: newComing,
                        minPoint: newComing,
                        maxYAxis: newComing.price,
                        minYAxis: newComing.price,
                    };
                }
                // 推送数据
                broadcast(newData, calculatedRenderData);
            };
            // 创建定时器
            const fetchInterval = (0, rxjs_1.interval)(options.wss_sample_rate);
            const loopSubscription = historyRequest
                .pipe(
            // 在收到历史数据后才开始定时器
            (0, operators_1.switchMap)(() => fetchInterval))
                .subscribe({
                next: pushWssData,
                error: () => { },
            });
            return () => {
                // 取消 wss 数据订阅
                wssSubscription === null || wssSubscription === void 0 ? void 0 : wssSubscription.unsubscribe();
                // 取消时间轮询订阅
                loopSubscription === null || loopSubscription === void 0 ? void 0 : loopSubscription.unsubscribe();
                // 取消买卖点的订阅
                currentUserTradesSubscription === null || currentUserTradesSubscription === void 0 ? void 0 : currentUserTradesSubscription.unsubscribe();
            };
        }, [
            currentUserTrades$,
            options.base,
            options.exchange,
            options.intervalType,
            options.isActive,
            options.isLongRangeMode,
            options.liveLineSampleRate,
            options.quote,
            options.requestClientType,
            options.screenTimeRange,
            options.showHistoriesOrder,
            options.wss_sample_rate,
            options.isMarkPrice,
        ]);
        // 返回可订阅的数据流
        return dataSubject.current;
    };
})(TradesProcessor = exports.TradesProcessor || (exports.TradesProcessor = {}));
